import type { BurnVelocityStatus, SolTemporalKpiSnapshot } from '@economi/domain-contracts';
import { formatSolesFromCents } from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './KpiTelemetryGrid.module.css';

interface KpiTelemetryGridProps {
  readonly snapshot: SolTemporalKpiSnapshot;
}

function getVelocityBadge(status: BurnVelocityStatus): { label: string; className: string } {
  switch (status) {
    case 'OPTIMAL':
      return { label: 'ÓPTIMO', className: styles.pillOptimal ?? '' };
    case 'ON_TRACK':
      return { label: 'EN LÍNEA', className: styles.pillOnTrack ?? '' };
    case 'WARNING':
      return { label: 'ALERTA', className: styles.pillWarning ?? '' };
    case 'CRITICAL':
      return { label: 'CRÍTICO', className: styles.pillCritical ?? '' };
  }
}

export function KpiTelemetryGrid({ snapshot }: KpiTelemetryGridProps) {
  const velocityBadge = getVelocityBadge(snapshot.burnVelocityStatus);
  const allowanceDeltaCents =
    snapshot.dynamicSafeDailyAllowanceCents - snapshot.baselineDailyAllowanceCents;
  const hoursPer100Soles = Number(((snapshot.minutesOfBudgetPerSol * 100) / 60).toFixed(1));

  return (
    <section className={styles.section} aria-label="KPIs de Impacto del Sol">
      <div className={styles.summaryStrip}>
        <div className={styles.stripItem}>
          <span className={styles.stripLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            Ingresos del Ciclo
            <InfoPopover title="Ingresos Totales del Ciclo">
              Recurrente: {formatSolesFromCents(snapshot.recurringInflowCents)} · Extraordinarios:{' '}
              {formatSolesFromCents(snapshot.windfallInflowCents)}.
            </InfoPopover>
          </span>
          <span className={styles.stripValue}>
            {formatSolesFromCents(snapshot.totalInflowCents)}
          </span>
        </div>

        <div className={styles.stripItem}>
          <span className={styles.stripLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            Tope ({snapshot.spendingRatePercent}%)
            <InfoPopover title="Tope de Gasto Efectivo">
              Política:{' '}
              {snapshot.keepRateConstantAcrossPeriods
                ? 'Tasa constante entre períodos'
                : 'Personalizado para este período'}.
            </InfoPopover>
          </span>
          <span className={styles.stripValue}>
            {formatSolesFromCents(snapshot.effectiveSpendingCapCents)}
          </span>
        </div>

        <div className={styles.stripItem}>
          <span className={styles.stripLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            Gasto Acumulado
            <InfoPopover title="Gasto vs. Disponible">
              Saldo restante antes del límite: {formatSolesFromCents(snapshot.remainingCapCents)}.
            </InfoPopover>
          </span>
          <span className={styles.stripValue}>
            {formatSolesFromCents(snapshot.totalSpentCents)}
          </span>
        </div>

        <div className={styles.stripItem}>
          <span className={styles.stripLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            Ahorro Protegido
            <InfoPopover title="Reserva Intocable del Ciclo">
              Día {snapshot.elapsedDays} de {snapshot.totalCycleDays} (faltan {snapshot.remainingDays} días).
            </InfoPopover>
          </span>
          <span className={`${styles.stripValue} ${styles.textPositive}`}>
            {formatSolesFromCents(snapshot.protectedSavingsCents)}
          </span>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        {/* KPI-02: Cuota Diaria Segura Actual (D_safe) */}
        <article className={`${styles.card} ${styles.heroCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.kpiCode}>KPI-02 · D_safe</span>
            <InfoPopover title="KPI-02: Cuota Diaria Segura Actual" align="right">
              <strong>Fórmula: L_rem / T_rem</strong>
              <span>
                Divide tu saldo disponible ({formatSolesFromCents(snapshot.remainingCapCents)}) entre los {snapshot.remainingDays} días restantes del ciclo.
              </span>
            </InfoPopover>
          </div>
          <h3 className={styles.cardTitle}>Cuota Diaria Segura</h3>
          <div className={styles.metricHero}>
            {formatSolesFromCents(snapshot.dynamicSafeDailyAllowanceCents)}
            <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
              {' '}/ día
            </span>
          </div>
          <div className={styles.metricSubRow}>
            <span>{snapshot.remainingDays} días restantes</span>
            <span
              className={
                allowanceDeltaCents >= 0 ? styles.textPositive : styles.textWarning
              }
            >
              {formatSolesFromCents(allowanceDeltaCents, true)} vs. D₀
            </span>
          </div>
        </article>

        {/* KPI-01: Cuota Diaria Inicial (D_0) */}
        <article className={`${styles.card} ${styles.standardCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.kpiCode}>KPI-01 · D_0</span>
            <InfoPopover title="KPI-01: Cuota Diaria Inicial" align="right">
              <strong>Fórmula: L_cap / T_total</strong>
              <span>
                Tu cuota diaria base al Día 1 considerando {snapshot.totalCycleDays} días de ciclo (inicio: {snapshot.startDateIso}).
              </span>
            </InfoPopover>
          </div>
          <h3 className={styles.cardTitle}>Cuota Diaria Base</h3>
          <div className={styles.metricPrimary}>
            {formatSolesFromCents(snapshot.baselineDailyAllowanceCents)}
            <span style={{ fontSize: '0.88rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
              {' '}/ día
            </span>
          </div>
          <div className={styles.metricSubRow}>
            <span>{snapshot.totalCycleDays} días</span>
            <span>{snapshot.startDateIso}</span>
          </div>
        </article>

        {/* KPI-03: Factor de Compresión Futura por Sol */}
        <article className={`${styles.card} ${styles.standardCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.kpiCode}>KPI-03 · ΔD_sol</span>
            <InfoPopover title="KPI-03: Compresión Diaria por Sol" align="right">
              <strong>Fórmula: 100 ¢ / T_rem</strong>
              <span>
                Cuánto se reduce tu cuota diaria de los próximos días por cada S/ 1.00 (o S/ 100) que gastas hoy.
              </span>
            </InfoPopover>
          </div>
          <h3 className={styles.cardTitle}>Compresión Futura</h3>
          <div className={styles.metricPrimary}>
            -{snapshot.dailyCompressionPerSolCents.toFixed(2)} ¢
            <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
              {' '}/ S/ 1
            </span>
          </div>
          <div className={styles.metricSubRow}>
            <span>Cada S/ 100 hoy:</span>
            <span className={styles.textWarning}>
              -{formatSolesFromCents(Math.round(snapshot.dailyCompressionPer100SolesCents))}/día
            </span>
          </div>
        </article>

        {/* KPI-04: Costo en Tiempo de Vida del Presupuesto */}
        <article className={`${styles.card} ${styles.compactCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.kpiCode}>KPI-04 · τ(g)</span>
            <InfoPopover title="KPI-04: Costo en Tiempo del Sol" align="right">
              <strong>Fórmula: (g / D_0) × 24h</strong>
              <span>Minutos u horas de vida de tu presupuesto que consume cada compra.</span>
            </InfoPopover>
          </div>
          <h3 className={styles.cardTitle}>Costo en Tiempo</h3>
          <div className={styles.metricPrimary}>
            {snapshot.minutesOfBudgetPerSol.toFixed(1)} min
          </div>
          <div className={styles.metricSubRow}>
            <span>Cada S/ 100:</span>
            <span className={styles.textWarning}>{hoursPer100Soles} hrs</span>
          </div>
        </article>

        {/* KPI-05: Índice de Velocidad de Quema (BRVI) */}
        <article className={`${styles.card} ${styles.compactCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.kpiCode}>KPI-05 · BRVI</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <span className={velocityBadge.className}>{velocityBadge.label}</span>
              <InfoPopover title="KPI-05: Velocidad de Quema (BRVI)" align="right">
                <span>Compara tu ritmo de gasto real frente al ritmo lineal ideal (1.00x).</span>
                <span>≤ 0.95x Óptimo · 0.95–1.05x En línea · &gt; 1.25x Crítico.</span>
              </InfoPopover>
            </div>
          </div>
          <h3 className={styles.cardTitle}>Velocidad de Quema</h3>
          <div className={styles.metricPrimary}>{snapshot.burnRateVelocityIndex.toFixed(2)}x</div>
          <div className={styles.metricSubRow}>
            <span>Ideal = 1.00x</span>
            <span>
              {Math.round(
                (snapshot.totalSpentCents / Math.max(1, snapshot.effectiveSpendingCapCents)) * 100
              )}
              % usado
            </span>
          </div>
        </article>

        {/* KPI-06: Autonomía Real Restante (Runway) */}
        <article className={`${styles.card} ${styles.compactCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.kpiCode}>KPI-06 · R_days</span>
            <InfoPopover title="KPI-06: Autonomía Restante (Runway)" align="right">
              <strong>Fórmula: L_rem / ḡ</strong>
              <span>Días que durará tu saldo disponible si mantienes tu promedio diario actual.</span>
            </InfoPopover>
          </div>
          <h3 className={styles.cardTitle}>Autonomía Real</h3>
          <div className={styles.metricPrimary}>{snapshot.realRunwayDays} días</div>
          <div className={styles.metricSubRow}>
            <span>Alcance:</span>
            <span
              className={
                snapshot.realRunwayDays >= snapshot.remainingDays
                  ? styles.textPositive
                  : styles.textDanger
              }
            >
              {snapshot.projectedExhaustionDateIso ?? snapshot.renewalDateIso}
            </span>
          </div>
        </article>

        {/* KPI-07: Brecha de Superávit/Déficit de Ritmo (B_t) */}
        <article className={`${styles.card} ${styles.compactCard}`}>
          <div className={styles.cardHeader}>
            <span className={styles.kpiCode}>KPI-07 · B_t</span>
            <InfoPopover title="KPI-07: Colchón de Ritmo (B_t)" align="right">
              <strong>Fórmula: (D_0 × t) - G_acum</strong>
              <span>Diferencia acumulada al día {snapshot.elapsedDays} entre el presupuesto lineal ideal y tu gasto real.</span>
            </InfoPopover>
          </div>
          <h3 className={styles.cardTitle}>Colchón de Ritmo</h3>
          <div
            className={`${styles.metricPrimary} ${
              snapshot.pacingBufferCents >= 0 ? styles.textPositive : styles.textDanger
            }`}
          >
            {formatSolesFromCents(snapshot.pacingBufferCents, true)}
          </div>
          <div className={styles.metricSubRow}>
            <span>Día {snapshot.elapsedDays}</span>
            <span>{snapshot.pacingBufferCents >= 0 ? 'Superávit' : 'Déficit'}</span>
          </div>
        </article>
      </div>
    </section>
  );
}
